import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authPathWithPlan, mapPricingPlans } from './sharedPricing';
import {
  finalCta,
  flowSteps,
  heroDescription,
  heroEyebrow,
  heroTitleLines,
  productHighlights,
  statsBar,
  testimonials,
} from './landingCopy';

export default function LandingAgency({
  brand = 'Workspace',
  logoUrl = null,
  pricingPlans = [],
  authPath = '/auth',
  primaryColor = '#059669',
} = {}) {
  const navigate = useNavigate();
  const plans = mapPricingPlans(pricingPlans);
  const title = heroTitleLines();
  const products = productHighlights(brand);
  const steps = flowSteps(brand);
  const quotes = testimonials(brand);
  const cta = finalCta(brand);
  const featured = plans.find((p) => p.highlight) || plans[1] || plans[0];

  return (
    <div className="min-h-screen bg-[#0c1210] text-white" data-testid="wl-landing-agency">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {logoUrl ? (
            <img src={logoUrl} alt={brand} className="w-9 h-9 rounded-xl object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold" style={{ background: primaryColor }}>
              {(brand || 'A')[0]}
            </div>
          )}
          <div>
            <p className="font-semibold leading-none">{brand}</p>
            <p className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">Agency portal</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
          <a href="#products" className="hover:text-white">Products</a>
          <a href="#flow" className="hover:text-white">How it works</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
        </div>
        <Button variant="outline" onClick={() => navigate(authPath)} className="border-white/20 rounded-full">
          Client login
        </Button>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-8 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: primaryColor }}>{heroEyebrow()}</p>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
            {title.line1}<br />
            <span style={{ color: primaryColor }}>{title.line2}</span>
          </h1>
          <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
            {heroDescription(brand)}
          </p>
          <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mb-8">
            <span className="px-3 py-1 rounded-full border border-white/10">Websites</span>
            <span className="px-3 py-1 rounded-full border border-white/10">Marketing</span>
            <span className="px-3 py-1 rounded-full border border-white/10">AI Tools</span>
            <span className="px-3 py-1 rounded-full border border-white/10">Lead Scraper</span>
          </div>
          <Button
            onClick={() => navigate(authPathWithPlan(authPath, featured?.key))}
            className="rounded-full font-bold text-black"
            style={{ background: primaryColor }}
          >
            Start for free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
          <p className="text-sm text-zinc-500 mb-4">Featured plan</p>
          {featured && (
            <>
              <h2 className="text-2xl font-bold mb-1">{featured.name}</h2>
              <p className="text-4xl font-extrabold mb-2" style={{ color: primaryColor }}>{featured.price}</p>
              <p className="text-sm text-zinc-400 mb-6">{featured.desc}</p>
              <ul className="space-y-2 mb-6">
                {(featured.features || []).slice(0, 5).map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(authPathWithPlan(authPath, featured.key))}
                className="w-full rounded-full font-bold text-black"
                style={{ background: primaryColor }}
              >
                {featured.cta} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </section>

      <section className="border-y border-white/10 py-12 px-6 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {statsBar().map((s) => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold" style={{ color: primaryColor }}>{s.value}</p>
              <p className="text-sm text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="products" className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold mb-3">Everything you need to launch and grow</h2>
        <p className="text-zinc-400 mb-10 max-w-2xl">
          From websites and AI tools to marketing and lead generation — {brand} puts your whole stack in one place.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: primaryColor }}>{p.tag}</p>
              <h3 className="text-lg font-bold mb-1">{p.title}</h3>
              <p className="text-sm mb-2" style={{ color: primaryColor }}>{p.subtitle}</p>
              <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="flow" className="border-y border-white/10 py-20 px-6 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-10">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {steps.map((s) => (
              <div key={s.step} className="rounded-2xl border border-white/10 p-5">
                <p className="text-3xl font-extrabold opacity-40 mb-2" style={{ color: primaryColor }}>{s.step}</p>
                <h3 className="font-bold mb-1">{s.title}</h3>
                <p className="text-xs mb-2" style={{ color: primaryColor }}>{s.module}</p>
                <p className="text-sm text-zinc-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold mb-3">Start free. Scale as you grow.</h2>
        <p className="text-zinc-400 mb-8">All plans — modules unlock when clients create an account.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan) => (
            <div key={plan.key || plan.name} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex flex-col">
              <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
              <div className="text-2xl font-extrabold mb-3">{plan.price}</div>
              <p className="text-sm text-zinc-500 mb-4">{plan.desc}</p>
              <ul className="space-y-2 flex-1 mb-6">
                {(plan.features || []).slice(0, 8).map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: primaryColor }} />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(authPathWithPlan(authPath, plan.key))}
                className="w-full rounded-full bg-white/10 hover:bg-white/20 text-white"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3">Loved by builders worldwide</h2>
          <p className="text-center text-sm text-zinc-500 mb-10 flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
            <span className="ml-2">4.9 / 5 from 2,400+ reviews</span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {quotes.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-sm font-semibold">{t.name}</p>
                <p className="text-xs text-zinc-500">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 text-center">
        <h2 className="text-4xl font-bold mb-4 max-w-3xl mx-auto">{cta.title}</h2>
        <p className="text-zinc-400 text-lg mb-8">{cta.subtitle}</p>
        <Button
          onClick={() => navigate(authPathWithPlan(authPath, featured?.key))}
          className="rounded-full px-10 py-6 text-lg font-bold text-black"
          style={{ background: primaryColor }}
        >
          Get started free <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        <p className="text-zinc-500 text-sm mt-4">{cta.note}</p>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-zinc-600">
        © {new Date().getFullYear()} {brand}
      </footer>
    </div>
  );
}
